import React, { useEffect, useContext, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useParams } from 'react-router';

import config from '../../config';
import { getActiveSite, setCreatedSite } from '../../bundles/sites';
import { getLayout } from '../../bundles/layouts';
import { getComponentsLoaded } from '../../bundles/components';
import { getVisualizationsLoaded } from '../../bundles/visualizations';
import { requestSources, getSourcesLoaded, getSiteVariablesUpdatedAt } from '../../bundles/sources';
import Loader from '../Loader';
import {
  setViewerReady,
  startSitePolling,
  stopSitePolling,
  initializeSitePolling,
  getTimezone,
} from '../../bundles/application';
import { getModelsLoaded } from '../../bundles/models';
import { getStateSetsLoaded } from '../../bundles/statesets';
import { requestIntegrations, getIntegrationsLoaded } from '../../bundles/integrations';
import { getOnline, displayNotification, getNotification } from '../../bundles/notifications';
import { getIsContributorOrAdmin } from '../../bundles/auth';
import AppContext from '../../AppContext';
import { datetimeString } from '../../datetimeUtils';

import Page from '../Page';
import { requestNotifyEvents, getEventNotifications } from '../../bundles/events';
import { requestNotifyAlarms } from '../../bundles/alarms';

const SiteDetailView = () => {
  const { id: siteId, page } = useParams();
  const dispatch = useDispatch();

  const layout = useSelector((state) => getLayout(state, siteId));
  const site = useSelector(getActiveSite);
  const sourcesLoaded = useSelector(getSourcesLoaded);
  const siteVariablesUpdatedAt = useSelector(getSiteVariablesUpdatedAt);
  const modelsLoaded = useSelector(getModelsLoaded);
  const componentsLoaded = useSelector(getComponentsLoaded);
  const visualizationsLoaded = useSelector(getVisualizationsLoaded);
  const integrationsLoaded = useSelector(getIntegrationsLoaded);
  const statesetsLoaded = useSelector(getStateSetsLoaded);
  const online = useSelector(getOnline);
  const timezone = useSelector(getTimezone);
  const eventNotifications = useSelector(getEventNotifications);
  const isContributorOrAdmin = useSelector((state) => getIsContributorOrAdmin(state, site));

  const { setIsFullScreen } = useContext(AppContext);
  const [loadingContributor, setLoadingContributor] = useState(true);

  useEffect(() => {
    dispatch(setCreatedSite(null));
    return () => {
      setIsFullScreen(false);
    };
  }, []);

  useEffect(() => {
    if (siteId) {
      dispatch(initializeSitePolling(siteId));
    }
  }, [siteId]);

  useEffect(() => {
    if (online) {
      dispatch(setViewerReady(false));
    }
  }, [online]);

  // We don't request sources in Router because Admin routes don't need to include withVariables
  useEffect(() => {
    if (online && siteId && (!siteVariablesUpdatedAt || sourcesLoaded !== siteId)) {
      dispatch(requestSources({ siteId, withVariables: true }));
    }
  }, [siteId, online, siteVariablesUpdatedAt, sourcesLoaded]);

  useEffect(() => {
    if (!config.onPremise && online && siteId) {
      dispatch(requestIntegrations({ site: siteId }));
    }
  }, [siteId, online]);

  useEffect(() => {
    if (online && siteId) {
      dispatch(requestNotifyEvents(siteId));
      dispatch(requestNotifyAlarms(siteId));
    }
  }, [siteId, online]);

  useEffect(() => {
    if (eventNotifications.todoCount > 0 || eventNotifications.overdueCount > 0) {
      const timeStamp = datetimeString(Date.now(), timezone);

      const notifications = getNotification(
        'eventState',
        'eventNotifications'
      )({
        todoCount: eventNotifications.todoCount,
        overdueCount: eventNotifications.overdueCount,
        timeStamp,
      });

      notifications.forEach((n) => dispatch(displayNotification(n)));
    }
  }, [eventNotifications]);

  const assetsLoaded =
    sourcesLoaded &&
    modelsLoaded &&
    componentsLoaded &&
    visualizationsLoaded &&
    statesetsLoaded &&
    (config.onPremise || integrationsLoaded);

  useEffect(() => {
    if (assetsLoaded) {
      dispatch(startSitePolling());
      setLoadingContributor(false);
    }
    return () => {
      dispatch(stopSitePolling());
    };
  }, [assetsLoaded]);

  const { pages } = layout || { pages: [], type: 'pt' };

  if (!page) {
    if (loadingContributor) {
      return (
        <Loader
          overlay
          text={site?.name ? `Retrieving ${site.name}...` : 'Loading...'}
        />
      );
    }
    const { isPublic, path } = pages?.[0] || {};
    if (isContributorOrAdmin && path && !isPublic) {
      return (
        <Navigate
          to={`/sites/${siteId}/${pages[0].path}`}
          replace
        />
      );
    } else {
      const publicPageIndex = pages.findIndex((currentPage) => currentPage.isPublic);
      return publicPageIndex === -1 ? (
        <Navigate
          to="/sites"
          replace
        />
      ) : (
        <Navigate
          to={`/sites/${siteId}/${pages[publicPageIndex].path}`}
          replace
        />
      );
    }
  }

  const pageConfig = pages && pages.find(({ path }) => path.toLowerCase() === page);

  return (
    <>
      <Page
        pages={pages}
        site={site}
        page={page}
        pageConfig={pageConfig}
      />
    </>
  );
};
export default SiteDetailView;
